import React, { useState, useEffect, useRef } from 'react';
import { Doughnut, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  LineElement,
  PointElement,
  Filler,
} from 'chart.js';
import DashboardLayout from '../layout/DashboardLayout';
import { useSelector, useDispatch } from 'react-redux';
import { getInitials } from '../../utils/stringUtils';
import { fetchLeads } from '../../store/slices/leadsSlice';
import { fetchAllProposals } from '../../store/slices/proposalsSlice';
import { fetchProjects } from '../../store/slices/projectsSlice';
import Spinner from '../common/Spinner';
import { Navigate, useNavigate } from 'react-router-dom';
import { logout } from '../../store/slices/authSlice';
import { showToast } from '../utils/Toast';
import EditProfileDialog from '../profile/EditProfileDialog';
import NotificationsDialog from '../notifications/NotificationsDialog';
import { fetchUnreadCount, fetchNotifications } from '../../store/slices/notificationsSlice';
import { useThemeColors } from '../../hooks/useThemeColors';

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  LineElement,
  PointElement,
  Filler
);

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

const hexToRgba = (hex, alpha = 1) => {
  const h = (hex || '#6366F1').replace('#', '');
  if (h.length !== 6) return `rgba(99,102,241,${alpha})`;
  const r = parseInt(h.substr(0, 2), 16);
  const g = parseInt(h.substr(2, 2), 16);
  const b = parseInt(h.substr(4, 2), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/* -------------------------------------------------------------------------- */
/* Sub-components                                                             */
/* -------------------------------------------------------------------------- */

// Tiny inline SVG sparkline
const Sparkline = ({ data = [], color = '#10B981', width = 120, height = 36 }) => {
  if (!data || data.length === 0 || data.every((v) => v === 0)) {
    return (
      <div style={{ width, height }} className="flex items-center justify-center text-[10px] text-gray-300">
        no data
      </div>
    );
  }
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const step = width / Math.max(1, data.length - 1);
  const points = data
    .map((v, i) => {
      const x = i * step;
      const y = height - ((v - min) / range) * (height - 4) - 2;
      return `${x},${y}`;
    })
    .join(' ');
  const gradId = `spark-${color.replace(/[^a-zA-Z0-9]/g, '')}`;
  return (
    <svg width={width} height={height} className="overflow-visible">
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.22" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon fill={`url(#${gradId})`} points={`0,${height} ${points} ${width},${height}`} />
      <polyline
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        points={points}
      />
    </svg>
  );
};

const StatCard = ({ icon, iconBg, iconColor, label, value, change, changePositive, changeLabel, sparkData, sparkColor, breakdown, onCardClick, onBreakdownClick }) => {
  return (
    <div
      onClick={onCardClick}
      className={`bg-white rounded-2xl border border-gray-200/80 shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition-all p-5 flex flex-col gap-3 min-w-0 ${
        onCardClick ? 'cursor-pointer hover:shadow-[0_10px_24px_-12px_rgba(15,23,42,0.18)] hover:-translate-y-0.5' : ''
      }`}
    >
      <div className="flex items-start gap-3">
        <div
          className="flex items-center justify-center w-10 h-10 rounded-xl shrink-0"
          style={{ backgroundColor: iconBg }}
        >
          <div style={{ color: iconColor }}>{icon}</div>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[11.5px] font-medium text-gray-500 truncate">{label}</p>
          <div className="flex items-baseline gap-2 mt-0.5">
            <p className="text-2xl font-bold text-gray-900 leading-none">
              {typeof value === 'number' ? value.toLocaleString() : value}
            </p>
            {(change || change === 0) && (
              <span
                className={`text-[11px] font-semibold ${
                  changePositive ? 'text-emerald-600' : 'text-rose-600'
                }`}
              >
                {changePositive ? '↑' : '↓'} {change}
              </span>
            )}
          </div>
          {changeLabel && (
            <p className="text-[10px] text-gray-400 mt-0.5">{changeLabel}</p>
          )}
        </div>
        <div className="shrink-0">
          <Sparkline data={sparkData} color={sparkColor} />
        </div>
      </div>

      {breakdown && breakdown.length > 0 && (
        <div className="space-y-0.5 pt-2 border-t border-gray-100 -mx-1.5">
          {breakdown.map((b, i) => (
            <button
              key={i}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (b.onClick) b.onClick();
                else if (onBreakdownClick) onBreakdownClick(b);
              }}
              className="w-full flex items-center justify-between text-[12px] px-1.5 py-1 rounded-md hover:bg-gray-50 transition-colors text-left"
            >
              <span className="flex items-center gap-2 text-gray-600">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: b.color }} />
                {b.label}
              </span>
              <span className="font-semibold text-gray-900">{b.value?.toLocaleString?.() ?? b.value}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const DonutCard = ({ icon, iconBg, iconColor, title, subtitle, chartData, breakdown, sliceActions }) => {
  const handleChartClick = (_e, els) => {
    if (!els || els.length === 0) return;
    const idx = els[0].index;
    const action = sliceActions?.[idx];
    if (typeof action === 'function') action();
  };
  return (
    <div className="bg-white rounded-2xl border border-gray-200/80 shadow-[0_1px_2px_rgba(15,23,42,0.04)] p-5 flex flex-col">
      <div className="flex items-start gap-3 mb-4">
        <div
          className="flex items-center justify-center w-9 h-9 rounded-xl shrink-0"
          style={{ backgroundColor: iconBg }}
        >
          <div style={{ color: iconColor }}>{icon}</div>
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-900 leading-tight">{title}</p>
          <p className="text-[11.5px] text-gray-500 mt-0.5">{subtitle}</p>
        </div>
      </div>

      <div className="flex items-center gap-5 flex-1">
        <div className="relative w-[120px] h-[120px] shrink-0">
          <Doughnut
            data={chartData}
            options={{
              plugins: { legend: { display: false }, tooltip: { enabled: true } },
              responsive: true,
              maintainAspectRatio: false,
              cutout: '72%',
              onHover: (e, els) => {
                if (e?.native?.target) e.native.target.style.cursor = els && els.length ? 'pointer' : 'default';
              },
              onClick: handleChartClick,
            }}
          />
        </div>
        <div className="flex-1 space-y-0.5 min-w-0 -mx-1.5">
          {(breakdown || []).map((b, i) => (
            <button
              key={i}
              type="button"
              onClick={b.onClick}
              disabled={!b.onClick}
              className={`w-full flex items-center justify-between text-[12.5px] px-1.5 py-1 rounded-md text-left ${
                b.onClick ? 'hover:bg-gray-50 transition-colors cursor-pointer' : ''
              }`}
            >
              <span className="flex items-center gap-2 text-gray-600 min-w-0">
                <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: b.color }} />
                <span className="truncate">{b.label}</span>
              </span>
              <span className="font-semibold text-gray-900 shrink-0 ml-2">{b.value}{b.suffix || ''}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/* Detail Dialog (Master-Detail with inline Sneak Peek)                       */
/* -------------------------------------------------------------------------- */

const TRACKING_COLOR = {
  new: '#10B981',
  waiting: '#38BDF8',
  recently_sent: '#60A5FA',
  critical: '#EF4444',
  follow_up: '#F59E0B',
  converted: '#10B981',
  lost: '#6B7280',
};

const PROPOSAL_COLOR = {
  draft: '#60A5FA',
  released: '#10B981',
  not_released: '#9CA3AF',
  approved_proposal_owner: '#8B5CF6',
  rejected_proposal_owner: '#EF4444',
  customer_rejected: '#EF4444',
  customer_approved: '#10B981',
};

const PROJECT_COLOR = {
  active: '#10B981',
  ready_to_schedule: '#60A5FA',
  on_hold: '#F59E0B',
  completed: '#8B5CF6',
  cancelled: '#EF4444',
  archived: '#6B7280',
};

const PRIORITY_COLOR = {
  low: '#94A3B8',
  medium: '#60A5FA',
  high: '#F59E0B',
  'very high': '#EF4444',
};

const labelize = (s = '') => s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const formatDate = (d) => {
  if (!d) return '—';
  const date = new Date(d);
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatCurrency = (n) => {
  if (n === null || n === undefined || isNaN(Number(n))) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(Number(n));
};

const getItemLabel = (item, type) => {
  if (type === 'lead' || type === 'project') {
    return item?.leadContact?.name || item?.siteContact?.name || item?.jobNumber || (type === 'project' ? 'Project' : 'Lead');
  }
  if (type === 'proposal') {
    return item?.title || item?.lead?.leadContact?.name || item?.lead?.jobNumber || `Proposal ${item?.proposalId || ''}`.trim();
  }
  return 'Item';
};

const getItemSubLabel = (item, type) => {
  if (type === 'lead' || type === 'project') {
    const parts = [];
    if (item?.jobNumber) parts.push(`#${item.jobNumber}`);
    const city = item?.address?.city || item?.siteContact?.address?.city;
    if (city) parts.push(city);
    return parts.join(' · ') || '—';
  }
  if (type === 'proposal') {
    return item?.proposalId ? `#${item.proposalId}` : (item?.lead?.jobNumber ? `Lead #${item.lead.jobNumber}` : '—');
  }
  return '';
};

const getItemStatus = (item, type) => {
  if (type === 'lead') return { label: labelize(item?.trackingStatus || ''), color: TRACKING_COLOR[item?.trackingStatus] || '#94A3B8' };
  if (type === 'proposal') return { label: labelize(item?.status || ''), color: PROPOSAL_COLOR[item?.status] || '#94A3B8' };
  if (type === 'project') return { label: labelize(item?.projectStatus || ''), color: PROJECT_COLOR[item?.projectStatus] || '#94A3B8' };
  return { label: '', color: '#94A3B8' };
};

const Field = ({ label, children, full }) => (
  <div className={full ? 'col-span-2' : ''}>
    <p className="text-[10.5px] font-semibold uppercase tracking-wider text-gray-400 mb-1">{label}</p>
    <div className="text-[13px] text-gray-900 break-words">{children || <span className="text-gray-400">—</span>}</div>
  </div>
);

const SneakPeek = ({ item, type, accentColor }) => {
  if (!item) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-center px-6 py-12">
        <div className="w-14 h-14 rounded-2xl bg-gray-50 flex items-center justify-center mb-3">
          <svg className="w-6 h-6 text-gray-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M21 21l-4.3-4.3M11 19a8 8 0 100-16 8 8 0 000 16z" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <p className="text-sm font-medium text-gray-700">Select an item</p>
        <p className="text-[11.5px] text-gray-400 mt-0.5">Click any row on the left to preview its details here.</p>
      </div>
    );
  }

  const label = getItemLabel(item, type);
  const status = getItemStatus(item, type);
  const city = item?.address?.city || item?.siteContact?.address?.city || item?.lead?.address?.city;
  const state = item?.address?.state || item?.siteContact?.address?.state || item?.lead?.address?.state;
  const street = item?.address?.street || item?.siteContact?.address?.street || item?.lead?.address?.street;
  const fullAddress = [street, [city, state].filter(Boolean).join(', ')].filter(Boolean).join(' · ');

  const initials = String(label).split(' ').slice(0, 2).map((s) => s[0]).join('').toUpperCase().slice(0, 2) || '?';

  return (
    <div className="flex-1 overflow-y-auto custom-scrollbar">
      {/* Header */}
      <div className="px-5 pt-5 pb-4 border-b border-gray-100">
        <div className="flex items-start gap-3">
          <div
            className="w-12 h-12 rounded-xl text-white flex items-center justify-center text-sm font-bold shrink-0"
            style={{ backgroundColor: accentColor }}
          >
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[15px] font-bold text-gray-900 leading-tight">{label}</p>
            <p className="text-[11.5px] text-gray-500 mt-0.5">{getItemSubLabel(item, type)}</p>
            <div className="flex items-center gap-1.5 mt-2 flex-wrap">
              {status.label && (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold"
                  style={{ backgroundColor: `${status.color}1A`, color: status.color }}
                >
                  <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: status.color }} />
                  {status.label}
                </span>
              )}
              {item?.priority && (
                <span
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold capitalize"
                  style={{ backgroundColor: `${PRIORITY_COLOR[item.priority] || '#94A3B8'}1A`, color: PRIORITY_COLOR[item.priority] || '#94A3B8' }}
                >
                  {item.priority}
                </span>
              )}
              {item?.isArchived && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-gray-100 text-gray-600">
                  Archived
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Body — type-specific fields */}
      <div className="px-5 py-4 space-y-4">
        {(type === 'lead' || type === 'project') && (
          <>
            <div className="grid grid-cols-2 gap-x-4 gap-y-3">
              <Field label="Job Number">{item?.jobNumber}</Field>
              <Field label="Created">{formatDate(item?.createdAt)}</Field>
              <Field label="Contact">{item?.leadContact?.name}</Field>
              <Field label="Phone">{item?.leadContact?.phone || item?.siteContact?.cellPhone}</Field>
              <Field label="Email" full>
                {item?.leadContact?.email ? (
                  <a href={`mailto:${item.leadContact.email.split(',')[0]}`} className="text-blue-600 hover:underline">{item.leadContact.email}</a>
                ) : null}
              </Field>
              <Field label="Address" full>{fullAddress}</Field>
              {type === 'lead' && <Field label="Last Modified">{formatDate(item?.lastModified || item?.updatedAt)}</Field>}
              {type === 'lead' && <Field label="Last Proposal Sent">{formatDate(item?.lastProposalSent)}</Field>}
              {type === 'project' && <Field label="Last Modified">{formatDate(item?.lastModified || item?.updatedAt)}</Field>}
              {type === 'project' && item?.originalLead && (
                <Field label="From Lead">
                  {item.originalLead?.jobNumber ? `#${item.originalLead.jobNumber}` : 'Linked lead'}
                </Field>
              )}
            </div>

            {item?.siteContact?.name && (
              <div className="pt-3 border-t border-gray-100">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-2">Site Contact</p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-[12.5px] text-gray-900">
                  <div><span className="text-gray-500">Name:</span> {item.siteContact.name}</div>
                  <div><span className="text-gray-500">Phone:</span> {item.siteContact.cellPhone || item.siteContact.altPhone || '—'}</div>
                  <div className="col-span-2"><span className="text-gray-500">Email:</span> {item.siteContact.email || '—'}</div>
                </div>
              </div>
            )}

            {Array.isArray(item?.projectManagers) && item.projectManagers.length > 0 && (
              <div className="pt-3 border-t border-gray-100">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-2">Project Managers</p>
                <div className="flex flex-wrap gap-1.5">
                  {item.projectManagers.map((pm, i) => {
                    const name = typeof pm === 'string'
                      ? pm
                      : `${pm?.profile?.firstName || ''} ${pm?.profile?.lastName || ''}`.trim() || pm?.email || 'PM';
                    return (
                      <span
                        key={pm?._id || i}
                        className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-gray-50 text-[12px] text-gray-700"
                      >
                        <span
                          className="w-5 h-5 rounded-full text-white flex items-center justify-center text-[9.5px] font-bold"
                          style={{ backgroundColor: accentColor }}
                        >
                          {name.split(' ').slice(0, 2).map((s) => s[0]).join('').toUpperCase().slice(0, 2) || '?'}
                        </span>
                        {name}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            {Array.isArray(item?.categories) && item.categories.length > 0 && (
              <div className="pt-3 border-t border-gray-100">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-2">Categories</p>
                <div className="flex flex-wrap gap-1.5">
                  {item.categories.map((c, i) => (
                    <span key={c?._id || i} className="px-2 py-0.5 rounded-md bg-gray-100 text-[11.5px] text-gray-700">
                      {typeof c === 'string' ? c : c?.name || 'Category'}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {Array.isArray(item?.notes) && item.notes.length > 0 && (
              <div className="pt-3 border-t border-gray-100">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-2">Latest Note</p>
                <p className="text-[12.5px] text-gray-700 line-clamp-3">
                  {item.notes[item.notes.length - 1]?.content || '—'}
                </p>
              </div>
            )}
          </>
        )}

        {type === 'proposal' && (
          <>
            <div className="grid grid-cols-2 gap-x-4 gap-y-3">
              <Field label="Proposal ID">{item?.proposalId}</Field>
              <Field label="Title" full>{item?.title}</Field>
              <Field label="Client Price">{formatCurrency(item?.clientPrice)}</Field>
              <Field label="Format">{item?.fileFormat ? item.fileFormat.toUpperCase() : '—'}</Field>
              <Field label="Created">{formatDate(item?.createdAt)}</Field>
              <Field label="Updated">{formatDate(item?.updatedAt)}</Field>
              <Field label="Email" full>
                {item?.emailAddress ? (
                  <a href={`mailto:${item.emailAddress}`} className="text-blue-600 hover:underline">{item.emailAddress}</a>
                ) : null}
              </Field>
            </div>

            {item?.lead && (
              <div className="pt-3 border-t border-gray-100">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-2">Linked Lead</p>
                <div className="text-[12.5px] text-gray-900">
                  <div className="font-medium">{item.lead?.leadContact?.name || '—'}</div>
                  <div className="text-gray-500 mt-0.5">
                    {item.lead?.jobNumber ? `#${item.lead.jobNumber}` : ''}
                    {item.lead?.address?.city ? ` · ${item.lead.address.city}` : ''}
                  </div>
                </div>
              </div>
            )}

            {(item?.releasedPdfUrl || item?.approvedPdfUrl) && (
              <div className="pt-3 border-t border-gray-100 flex flex-wrap gap-2">
                {item?.releasedPdfUrl && (
                  <a
                    href={item.releasedPdfUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11.5px] font-semibold border border-gray-200 bg-white hover:bg-gray-50 text-gray-700"
                  >
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6"/></svg>
                    Released PDF
                  </a>
                )}
                {item?.approvedPdfUrl && (
                  <a
                    href={item.approvedPdfUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11.5px] font-semibold border border-gray-200 bg-white hover:bg-gray-50 text-gray-700"
                  >
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><path d="M8 12l3 3 5-6"/></svg>
                    Approved PDF
                  </a>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const DetailDialog = ({ open, onClose, title, subtitle, items, type, accentColor }) => {
  const [selected, setSelected] = useState(null);

  // Auto-select first item whenever the dialog opens or items change
  useEffect(() => {
    if (open) setSelected(items && items.length > 0 ? items[0] : null);
  }, [open, items]);

  // Esc to close
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm animate-in"
      onClick={onClose}
    >
      <div
        className="w-full max-w-5xl h-[82vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-gray-100 flex items-start justify-between gap-3 shrink-0">
          <div className="min-w-0">
            <p className="text-base font-semibold text-gray-900 leading-tight">{title}</p>
            {subtitle && <p className="text-[12px] text-gray-500 mt-0.5">{subtitle}</p>}
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100 text-gray-500 shrink-0"
            aria-label="Close"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body — Master/Detail layout */}
        <div className="flex-1 flex min-h-0">
          {/* List */}
          <div className="w-full sm:w-[340px] border-r border-gray-100 flex flex-col min-h-0">
            <div className="flex-1 overflow-y-auto custom-scrollbar">
              {(!items || items.length === 0) ? (
                <div className="px-6 py-12 text-center">
                  <div className="w-14 h-14 rounded-2xl bg-gray-100 mx-auto flex items-center justify-center mb-3">
                    <svg className="w-6 h-6 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <circle cx="12" cy="12" r="10" />
                      <path d="M8 12h8" strokeLinecap="round" />
                    </svg>
                  </div>
                  <p className="text-sm font-medium text-gray-900">No items</p>
                  <p className="text-xs text-gray-500 mt-0.5">Nothing matches the current filter.</p>
                </div>
              ) : (
                <ul className="divide-y divide-gray-50">
                  {items.map((item, idx) => {
                    const label = getItemLabel(item, type);
                    const sub = getItemSubLabel(item, type);
                    const stat = getItemStatus(item, type);
                    const isActive = selected && (selected._id ? selected._id === item._id : selected === item);
                    return (
                      <li key={item._id || idx}>
                        <button
                          type="button"
                          onClick={() => setSelected(item)}
                          className={`w-full px-4 py-3 flex items-center gap-3 text-left transition-colors ${
                            isActive ? 'bg-gray-50' : 'hover:bg-gray-50/60'
                          }`}
                          style={isActive ? { boxShadow: `inset 3px 0 0 ${accentColor}` } : undefined}
                        >
                          <div
                            className="w-9 h-9 rounded-xl text-white flex items-center justify-center text-[12px] font-bold shrink-0"
                            style={{ backgroundColor: accentColor }}
                          >
                            {String(label).split(' ').slice(0, 2).map((s) => s[0]).join('').toUpperCase().slice(0, 2) || '?'}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[13px] font-medium text-gray-900 truncate">{label}</p>
                            <p className="text-[11px] text-gray-500 truncate">{sub}</p>
                          </div>
                          {stat.label && (
                            <span
                              className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full shrink-0"
                              style={{ backgroundColor: `${stat.color}1A`, color: stat.color }}
                            >
                              {stat.label}
                            </span>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>

          {/* Detail */}
          <div className="hidden sm:flex flex-1 flex-col min-h-0 bg-white">
            <SneakPeek item={selected} type={type} accentColor={accentColor} />
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between shrink-0">
          <p className="text-[11.5px] text-gray-500">
            {items?.length || 0} item{items?.length === 1 ? '' : 's'}
          </p>
          <button
            onClick={onClose}
            className="text-[12px] font-semibold px-3 py-1.5 rounded-lg hover:bg-gray-100 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/* Main component                                                             */
/* -------------------------------------------------------------------------- */

const DashboardHome = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const { leads, loading: leadsLoading } = useSelector((state) => state.leads);
  const { proposals, loading: proposalsLoading } = useSelector((state) => state.proposals);
  const { projects, loading: projectsLoading } = useSelector((state) => state.projects);
  const { unreadCount: notificationUnreadCount } = useSelector((state) => state.notifications);
  const { buttonColors } = useThemeColors();
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showEditProfile, setShowEditProfile] = useState(false);

  // Project Manager Filter State
  const [selectedProjectManager, setSelectedProjectManager] = useState(null);
  const [showProjectManagerDropdown, setShowProjectManagerDropdown] = useState(false);
  const [pmSearchTerm, setPmSearchTerm] = useState('');

  // Date Filter State
  const [dateRange, setDateRange] = useState({ startDate: null, endDate: null });
  const [showDatePicker, setShowDatePicker] = useState(false);

  // Detail dialog state (master-detail with inline sneak peek)
  const [dialog, setDialog] = useState({ open: false, title: '', subtitle: '', items: [], type: 'lead' });
  const [showNotifications, setShowNotifications] = useState(false);

  // Refs
  const profileDropdownRef = useRef(null);
  const projectManagerDropdownRef = useRef(null);
  const datePickerDropdownRef = useRef(null);

  const accentColor = buttonColors?.primary || '#6366F1';

  const isAuthorized = user && (user.profile.role === 'admin' || user.profile.role === 'super_admin');

  useEffect(() => {
    function handleClickOutside(event) {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
        setShowProfileDropdown(false);
      }
      if (projectManagerDropdownRef.current && !projectManagerDropdownRef.current.contains(event.target)) {
        setShowProjectManagerDropdown(false);
      }
      if (datePickerDropdownRef.current && !datePickerDropdownRef.current.contains(event.target)) {
        setShowDatePicker(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getAllProjectManagers = () => {
    const managersMap = new Map();
    leads?.forEach((lead) => {
      lead.projectManagers?.forEach((pm) => {
        if (pm && pm._id && pm.profile?.firstName && pm.profile?.lastName) managersMap.set(pm._id, pm);
      });
    });
    projects?.forEach((project) => {
      project.projectManagers?.forEach((pm) => {
        if (pm && pm._id && pm.profile?.firstName && pm.profile?.lastName) managersMap.set(pm._id, pm);
      });
    });
    proposals?.forEach((proposal) => {
      proposal.lead?.projectManagers?.forEach((pm) => {
        if (pm && pm._id && pm.profile?.firstName && pm.profile?.lastName) managersMap.set(pm._id, pm);
      });
    });
    return Array.from(managersMap.values()).sort((a, b) => {
      const nA = `${a.profile?.firstName || ''} ${a.profile?.lastName || ''}`.toLowerCase();
      const nB = `${b.profile?.firstName || ''} ${b.profile?.lastName || ''}`.toLowerCase();
      return nA.localeCompare(nB);
    });
  };

  const getFilteredProjectManagers = () => {
    const allManagers = getAllProjectManagers();
    if (!pmSearchTerm.trim()) return allManagers;
    const s = pmSearchTerm.toLowerCase();
    return allManagers.filter((m) => {
      const fullName = `${m.profile?.firstName || ''} ${m.profile?.lastName || ''}`.toLowerCase();
      const role = m.profile?.role?.replace('_', ' ').toLowerCase() || '';
      return fullName.includes(s) || role.includes(s);
    });
  };

  useEffect(() => {
    if (isAuthorized) {
      const projectManagerId = selectedProjectManager?._id;
      const { startDate, endDate } = dateRange;
      dispatch(fetchLeads({
        limit: 999999,
        ...(projectManagerId && { projectManagerId }),
        ...(startDate && { startDate: startDate.toISOString() }),
        ...(endDate && { endDate: endDate.toISOString() }),
      }));
      dispatch(fetchAllProposals({
        ...(projectManagerId && { projectManagerId }),
        ...(startDate && { startDate: startDate.toISOString() }),
        ...(endDate && { endDate: endDate.toISOString() }),
      }));
      dispatch(fetchProjects({
        limit: 999999,
        ...(projectManagerId && { projectManagerId }),
        ...(startDate && { startDate: startDate.toISOString() }),
        ...(endDate && { endDate: endDate.toISOString() }),
      }));
    }
  }, [dispatch, isAuthorized, selectedProjectManager, dateRange]);

  // Notifications: initial fetch + lightweight polling for unread count
  useEffect(() => {
    if (!isAuthorized) return undefined;
    dispatch(fetchNotifications({ limit: 50 }));
    dispatch(fetchUnreadCount());
    const id = setInterval(() => dispatch(fetchUnreadCount()), 60000);
    return () => clearInterval(id);
  }, [dispatch, isAuthorized]);

  const handleLogout = () => {
    dispatch(logout());
    showToast('Logged out successfully', 'success');
    navigate('/login');
  };

  if (!isAuthorized) return <Navigate to="/leads" />;

  const isLoading = leadsLoading || proposalsLoading || projectsLoading;

  const getDateRangeDisplay = () => {
    const { startDate, endDate } = dateRange;
    if (!startDate && !endDate) return 'All Time';
    if (startDate && endDate) return `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`;
    if (startDate) return `From ${startDate.toLocaleDateString()}`;
    if (endDate) return `Until ${endDate.toLocaleDateString()}`;
    return 'All Time';
  };

  const clearDateRange = () => setDateRange({ startDate: null, endDate: null });

  const getFilteredLeads = () => leads || [];
  const getFilteredProposals = () => proposals || [];
  const getFilteredProjects = () => projects || [];

  /* ---------- Status data builders (used by donut charts) ---------- */
  const getLeadTrackingStatusData = () => {
    const statusCounts = { new: 0, waiting: 0, recently_sent: 0, critical: 0, follow_up: 0, converted: 0, lost: 0 };
    getFilteredLeads().forEach((lead) => { if (statusCounts.hasOwnProperty(lead.trackingStatus)) statusCounts[lead.trackingStatus]++; });
    return {
      labels: ['New', 'Waiting', 'Recently Sent', 'Critical', 'Follow Up', 'Converted', 'Lost'],
      datasets: [{
        data: [statusCounts.new, statusCounts.waiting, statusCounts.recently_sent, statusCounts.critical, statusCounts.follow_up, statusCounts.converted, statusCounts.lost],
        backgroundColor: ['#4ADE80', '#38BDF8', '#60A5FA', '#EF4444', '#F59E0B', '#8B5CF6', '#6B7280'],
        borderWidth: 0,
      }],
      _raw: statusCounts,
    };
  };

  const getProposalStatusData = () => {
    const statusCounts = { draft: 0, released: 0, customer_approved: 0, customer_rejected: 0, not_released: 0, approved_proposal_owner: 0, rejected_proposal_owner: 0 };
    getFilteredProposals().forEach((proposal) => { if (statusCounts.hasOwnProperty(proposal.status)) statusCounts[proposal.status]++; });
    return {
      labels: ['Draft', 'Released', 'Client Approved', 'Customer Rejected', 'Not Released', 'Manually Approved', 'Owner Rejected'],
      datasets: [{
        data: [statusCounts.draft, statusCounts.released, statusCounts.customer_approved, statusCounts.customer_rejected, statusCounts.not_released, statusCounts.approved_proposal_owner, statusCounts.rejected_proposal_owner],
        backgroundColor: ['#60A5FA', '#4ADE80', '#8B5CF6', '#EF4444', '#F59E0B', '#38BDF8', '#FB7185'],
        borderWidth: 0,
      }],
      _raw: statusCounts,
    };
  };

  const getProjectStatusData = () => {
    const statusCounts = { active: 0, ready_to_schedule: 0, on_hold: 0, completed: 0, cancelled: 0, archived: 0 };
    getFilteredProjects().forEach((project) => { if (statusCounts.hasOwnProperty(project.projectStatus)) statusCounts[project.projectStatus]++; });
    return {
      labels: ['Active', 'Ready to Schedule', 'On Hold', 'Completed', 'Cancelled', 'Archived'],
      datasets: [{
        data: [statusCounts.active, statusCounts.ready_to_schedule, statusCounts.on_hold, statusCounts.completed, statusCounts.cancelled, statusCounts.archived],
        backgroundColor: ['#4ADE80', '#A855F7', '#F59E0B', '#8B5CF6', '#EF4444', '#6B7280'],
        borderWidth: 0,
      }],
      _raw: statusCounts,
    };
  };

  /* ---------- Stats counters (preserved logic) ---------- */
  const getLeadConversionRate = () => {
    const fl = getFilteredLeads();
    const fp = getFilteredProjects();
    if (!fl || fl.length === 0) return 0;
    const converted = fl.filter((lead) => {
      if (lead.trackingStatus === 'converted') return true;
      return fp?.some((project) => project.lead?._id === lead._id || project.lead === lead._id);
    }).length;
    return Math.round((converted / fl.length) * 100);
  };

  const getProposalApprovalRate = () => {
    const fp = getFilteredProposals();
    if (!fp || fp.length === 0) return 0;
    const released = fp.filter((p) => ['released', 'customer_approved', 'customer_rejected', 'approved_proposal_owner', 'rejected_proposal_owner'].includes(p.status));
    if (released.length === 0) return 0;
    const approved = released.filter((p) => ['customer_approved', 'approved_proposal_owner'].includes(p.status));
    return Math.round((approved.length / released.length) * 100);
  };

  const getProposalsSentThisMonth = () => {
    const fp = getFilteredProposals();
    if (!fp || fp.length === 0) return 0;
    const now = new Date();
    return fp.filter((p) => {
      if (!['released', 'customer_approved', 'customer_rejected'].includes(p.status)) return false;
      const d = new Date(p.updatedAt);
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    }).length;
  };

  const getDraftProposalsCount = () => getFilteredProposals().filter((p) => p.status === 'draft').length;
  const getApprovedProposalsCount = () => getFilteredProposals().filter((p) => p.status === 'customer_approved' || p.status === 'approved_proposal_owner').length;
  const getCriticalLeadsCount = () => getFilteredLeads().filter((l) => l.trackingStatus === 'critical').length;
  const getWaitingLeadsCount = () => getFilteredLeads().filter((l) => l.trackingStatus === 'waiting').length;
  const getHighPriorityLeadsCount = () => getFilteredLeads().filter((l) => l.priority === 'high' || l.priority === 'very high').length;
  const getLeadsCreatedThisMonth = () => {
    const fl = getFilteredLeads();
    if (!fl || fl.length === 0) return 0;
    const now = new Date();
    return fl.filter((l) => {
      const d = new Date(l.createdAt);
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    }).length;
  };
  const getOnHoldProjectsCount = () => getFilteredProjects().filter((p) => p.projectStatus === 'on_hold').length;
  const getCompletedProjectsCount = () => getFilteredProjects().filter((p) => p.projectStatus === 'completed').length;
  const getProjectsStartedThisMonth = () => {
    const fp = getFilteredProjects();
    if (!fp || fp.length === 0) return 0;
    const now = new Date();
    return fp.filter((p) => {
      const d = new Date(p.createdAt);
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    }).length;
  };
  const getHighPriorityProjectsCount = () => getFilteredProjects().filter((p) => p.priority === 'high' || p.priority === 'very high').length;

  /* ---------- Monthly trend ---------- */
  const getMonthlyLeadsTrend = () => {
    const labels = [];
    const monthsData = [];
    for (let i = 5; i >= 0; i--) {
      const date = new Date();
      date.setMonth(date.getMonth() - i);
      date.setDate(1);
      const label = `${date.toLocaleString('default', { month: 'short' })} ${date.getFullYear().toString().slice(-2)}`;
      labels.push(label);
      monthsData.push({ month: date.getMonth(), year: date.getFullYear(), label, count: 0 });
    }
    getFilteredLeads().forEach((lead) => {
      const d = new Date(lead.createdAt);
      const idx = monthsData.findIndex((m) => m.month === d.getMonth() && m.year === d.getFullYear());
      if (idx !== -1) monthsData[idx].count++;
    });
    return { labels, counts: monthsData.map((m) => m.count), months: monthsData };
  };

  // Last 14 days bucket counts for sparklines
  const sparkFromCreatedAt = (items, predicate = () => true, days = 14) => {
    const buckets = new Array(days).fill(0);
    const todayStart = (() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d.getTime(); })();
    (items || []).forEach((it) => {
      if (!predicate(it)) return;
      if (!it?.createdAt) return;
      const d = new Date(it.createdAt); d.setHours(0, 0, 0, 0);
      const diffDays = Math.round((todayStart - d.getTime()) / 86400000);
      if (diffDays >= 0 && diffDays < days) buckets[days - 1 - diffDays] += 1;
    });
    return buckets;
  };

  /* ---------- Detail dialog helpers ---------- */
  const openDialog = (title, items, type, subtitle = '') => {
    setDialog({ open: true, title, items: items || [], type, subtitle });
  };
  const closeDialog = () => setDialog((d) => ({ ...d, open: false }));

  /* ---------- Derived numbers ---------- */
  const totalLeads = (leads || []).length;
  const totalProposals = (proposals || []).length;
  const totalProjects = (projects || []).length;
  const leadsThisMonth = getLeadsCreatedThisMonth();
  const projectsThisMonth = getProjectsStartedThisMonth();
  const proposalApproval = getProposalApprovalRate();
  const proposalsSent = getProposalsSentThisMonth();
  const draftProposals = getDraftProposalsCount();
  const approvedProposals = getApprovedProposalsCount();
  const criticalLeads = getCriticalLeadsCount();
  const waitingLeads = getWaitingLeadsCount();
  const highPriorityLeads = getHighPriorityLeadsCount();
  const onHoldProjects = getOnHoldProjectsCount();
  const completedProjects = getCompletedProjectsCount();
  const highPriorityProjects = getHighPriorityProjectsCount();
  const conversionRate = getLeadConversionRate();
  const monthlyTrend = getMonthlyLeadsTrend();

  const leadStatusData = getLeadTrackingStatusData();
  const proposalStatusData = getProposalStatusData();
  const projectStatusData = getProjectStatusData();

  // Pct helpers
  const pct = (n, total) => (total > 0 ? `${Math.round((n / total) * 100)}%` : '0%');

  // Line chart data for monthly leads
  const monthlyChartData = {
    labels: monthlyTrend.labels,
    datasets: [
      {
        label: 'Leads',
        data: monthlyTrend.counts,
        borderColor: accentColor,
        backgroundColor: (ctx) => {
          const chart = ctx.chart;
          const { ctx: c, chartArea } = chart;
          if (!chartArea) return hexToRgba(accentColor, 0.15);
          const g = c.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
          g.addColorStop(0, hexToRgba(accentColor, 0.28));
          g.addColorStop(1, hexToRgba(accentColor, 0));
          return g;
        },
        fill: true,
        tension: 0.4,
        pointRadius: 4,
        pointBackgroundColor: '#ffffff',
        pointBorderColor: accentColor,
        pointBorderWidth: 2,
        pointHoverRadius: 6,
        borderWidth: 2.5,
      },
    ],
  };

  const monthlyChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    onHover: (e, els) => {
      e.native.target.style.cursor = els && els.length ? 'pointer' : 'default';
    },
    onClick: (_e, els) => {
      if (!els || els.length === 0) return;
      const idx = els[0].index;
      const m = monthlyTrend.months?.[idx];
      if (!m) return;
      const items = getFilteredLeads().filter((l) => {
        const d = new Date(l.createdAt);
        return d.getMonth() === m.month && d.getFullYear() === m.year;
      });
      openDialog(`Leads · ${m.label}`, items, 'lead', `${items.length} leads created`);
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#0f172a',
        padding: 10,
        titleFont: { size: 11 },
        bodyFont: { size: 13, weight: 'bold' },
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          label: (ctx) => `${ctx.parsed.y} Leads · click to explore`,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: { precision: 0, color: '#9ca3af', font: { size: 11 } },
        grid: { color: '#f3f4f6', drawBorder: false },
        border: { display: false },
      },
      x: {
        ticks: { color: '#9ca3af', font: { size: 11 } },
        grid: { display: false },
        border: { display: false },
      },
    },
  };

  /* ---------- Render ---------- */
  return (
    <DashboardLayout>
      <div className="min-h-full bg-[#f6f8fb] p-4 sm:p-6">
        {isLoading ? (
          <div className="flex justify-center items-center h-96">
            <div className="text-center">
              <Spinner size="xl" color="blue" />
              <p className="text-gray-500 mt-4">Loading dashboard...</p>
            </div>
          </div>
        ) : (
          <div className="max-w-[1400px] mx-auto space-y-5">
            {/* Header */}
            <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
              <div>
                <h1 className="text-[26px] font-bold text-gray-900 leading-tight flex items-center gap-2">
                  Welcome back, <span style={{ color: accentColor }}>{user?.profile?.firstName}</span>
                  <span role="img" aria-label="wave" className="text-[22px]">👋</span>
                </h1>
                <p className="text-[13px] text-gray-500 flex items-center gap-1.5 mt-1">
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" />
                    <path d="M16 2v4M8 2v4M3 10h18" />
                  </svg>
                  {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                </p>
              </div>

              {/* Filters row */}
              <div className="flex items-center gap-2.5">
                {/* Date Range */}
                <div className="relative" ref={datePickerDropdownRef}>
                  <button
                    onClick={() => setShowDatePicker(!showDatePicker)}
                    className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-xl border bg-white text-[13px] font-medium shadow-sm transition-colors ${
                      (dateRange.startDate || dateRange.endDate)
                        ? 'border-transparent text-gray-900'
                        : 'border-gray-200 text-gray-700 hover:border-gray-300'
                    }`}
                    style={(dateRange.startDate || dateRange.endDate) ? { borderColor: accentColor, color: accentColor } : undefined}
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="4" width="18" height="18" rx="2" />
                      <path d="M16 2v4M8 2v4M3 10h18" />
                    </svg>
                    {getDateRangeDisplay()}
                  </button>
                  {showDatePicker && (
                    <div className="absolute right-0 mt-2 w-72 bg-white border border-gray-200 rounded-xl shadow-xl z-30 p-4">
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs font-semibold text-gray-700 mb-1.5">Start Date</label>
                          <input
                            type="date"
                            value={dateRange.startDate ? dateRange.startDate.toISOString().split('T')[0] : ''}
                            onChange={(e) => {
                              const d = e.target.value ? new Date(e.target.value) : null;
                              setDateRange((prev) => ({ ...prev, startDate: d }));
                            }}
                            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2"
                            style={{ '--tw-ring-color': accentColor }}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-700 mb-1.5">End Date</label>
                          <input
                            type="date"
                            value={dateRange.endDate ? dateRange.endDate.toISOString().split('T')[0] : ''}
                            onChange={(e) => {
                              const d = e.target.value ? new Date(e.target.value) : null;
                              setDateRange((prev) => ({ ...prev, endDate: d }));
                            }}
                            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2"
                            style={{ '--tw-ring-color': accentColor }}
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-2 pt-2 border-t border-gray-100">
                          <button
                            onClick={() => { const t = new Date(); const a = new Date(t); a.setDate(t.getDate() - 30); setDateRange({ startDate: a, endDate: t }); }}
                            className="px-3 py-1.5 text-xs bg-gray-50 hover:bg-gray-100 rounded-lg font-medium"
                          >Last 30 days</button>
                          <button
                            onClick={() => { const t = new Date(); const a = new Date(t); a.setDate(t.getDate() - 90); setDateRange({ startDate: a, endDate: t }); }}
                            className="px-3 py-1.5 text-xs bg-gray-50 hover:bg-gray-100 rounded-lg font-medium"
                          >Last 90 days</button>
                          <button
                            onClick={() => { const t = new Date(); setDateRange({ startDate: new Date(t.getFullYear(), 0, 1), endDate: t }); }}
                            className="px-3 py-1.5 text-xs bg-gray-50 hover:bg-gray-100 rounded-lg font-medium"
                          >This year</button>
                          <button onClick={clearDateRange} className="px-3 py-1.5 text-xs bg-red-50 hover:bg-red-100 text-red-700 rounded-lg font-medium">Clear</button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* PM Filter */}
                <div className="relative" ref={projectManagerDropdownRef}>
                  <button
                    onClick={() => setShowProjectManagerDropdown(!showProjectManagerDropdown)}
                    className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-xl border bg-white text-[13px] font-medium shadow-sm transition-colors ${
                      selectedProjectManager
                        ? 'border-transparent'
                        : 'border-gray-200 text-gray-700 hover:border-gray-300'
                    }`}
                    style={selectedProjectManager ? { borderColor: accentColor, color: accentColor } : undefined}
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" />
                    </svg>
                    {selectedProjectManager
                      ? `${selectedProjectManager.profile?.firstName} ${selectedProjectManager.profile?.lastName}`
                      : 'Filter'}
                    {selectedProjectManager && (
                      <span
                        className="ml-1 hover:bg-gray-100 rounded p-0.5"
                        onClick={(e) => { e.stopPropagation(); setSelectedProjectManager(null); }}
                      >
                        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                          <path d="M18 6L6 18M6 6l12 12" />
                        </svg>
                      </span>
                    )}
                  </button>
                  {showProjectManagerDropdown && (
                    <div className="absolute right-0 mt-2 w-72 bg-white border border-gray-200 rounded-xl shadow-xl z-30">
                      <div className="p-3 border-b border-gray-100">
                        <input
                          type="text"
                          value={pmSearchTerm}
                          onChange={(e) => setPmSearchTerm(e.target.value)}
                          placeholder="Search project managers..."
                          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2"
                          style={{ '--tw-ring-color': accentColor }}
                        />
                      </div>
                      <div className="max-h-64 overflow-auto">
                        <button
                          onClick={() => { setSelectedProjectManager(null); setShowProjectManagerDropdown(false); }}
                          className="w-full text-left px-4 py-2.5 hover:bg-gray-50 flex items-center gap-2 text-sm"
                        >
                          <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center">
                            <svg className="w-3.5 h-3.5 text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5" strokeLinecap="round"/></svg>
                          </div>
                          <span className="text-gray-700">All Project Managers</span>
                          {!selectedProjectManager && (
                            <span className="ml-auto w-1.5 h-1.5 rounded-full" style={{ backgroundColor: accentColor }} />
                          )}
                        </button>
                        {getFilteredProjectManagers().map((m) => (
                          <button
                            key={m._id}
                            onClick={() => { setSelectedProjectManager(m); setPmSearchTerm(''); setShowProjectManagerDropdown(false); }}
                            className="w-full text-left px-4 py-2.5 hover:bg-gray-50 flex items-center gap-2 text-sm"
                          >
                            <div className="w-7 h-7 rounded-full text-white flex items-center justify-center text-[11px] font-semibold" style={{ backgroundColor: accentColor }}>
                              {getInitials(m.profile?.firstName, m.profile?.lastName)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-gray-900 truncate">{m.profile?.firstName} {m.profile?.lastName}</div>
                              <div className="text-[11px] text-gray-500 truncate">{m.profile?.role?.replace('_', ' ')}</div>
                            </div>
                            {selectedProjectManager?._id === m._id && (
                              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: accentColor }} />
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Notifications bell */}
                <button
                  onClick={() => setShowNotifications(true)}
                  className="relative inline-flex items-center justify-center w-9 h-9 rounded-xl bg-white border border-gray-200 hover:border-gray-300 shadow-sm transition-colors"
                  title="Notifications"
                  aria-label="Notifications"
                >
                  <svg className="w-4 h-4 text-gray-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 8a6 6 0 00-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
                    <path d="M13.73 21a2 2 0 01-3.46 0" />
                  </svg>
                  {notificationUnreadCount > 0 && (
                    <span
                      className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full text-[9px] font-bold text-white flex items-center justify-center ring-2 ring-white"
                      style={{ backgroundColor: '#EF4444' }}
                    >
                      {Math.min(99, notificationUnreadCount)}
                    </span>
                  )}
                </button>

                {/* Profile dropdown trigger (legacy — kept for quick actions) */}
                <div className="relative hidden" ref={profileDropdownRef}>
                  <button
                    onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                    className="relative inline-flex items-center justify-center w-9 h-9 rounded-xl bg-white border border-gray-200 hover:border-gray-300 shadow-sm transition-colors"
                  >
                    <svg className="w-4 h-4 text-gray-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 8a6 6 0 00-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
                      <path d="M13.73 21a2 2 0 01-3.46 0" />
                    </svg>
                  </button>
                  {showProfileDropdown && (
                    <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-gray-100 z-30 overflow-hidden">
                      <div className="px-4 py-3 border-b border-gray-100">
                        <p className="text-sm font-semibold text-gray-900">Quick actions</p>
                      </div>
                      <div className="p-1.5">
                        <button onClick={() => { setShowProfileDropdown(false); setShowEditProfile(true); }} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-gray-700 hover:bg-gray-50 text-left">
                          Edit Profile
                        </button>
                        <button onClick={() => { setShowProfileDropdown(false); navigate('/leads'); }} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-gray-700 hover:bg-gray-50 text-left">
                          View all leads
                        </button>
                        <button onClick={() => { setShowProfileDropdown(false); handleLogout(); }} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-red-600 hover:bg-red-50 text-left">
                          Sign out
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Stat cards grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
              <StatCard
                icon={<svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="8" r="3.5"/><path d="M2.5 20a6.5 6.5 0 0113 0"/><circle cx="17" cy="9" r="2.5"/><path d="M15 20a4.5 4.5 0 016.5-4"/></svg>}
                iconBg="#ECFDF5"
                iconColor="#10B981"
                label="Total Leads"
                value={totalLeads}
                change={`${leadsThisMonth}`}
                changePositive={leadsThisMonth >= 0}
                changeLabel="new this month"
                sparkData={sparkFromCreatedAt(leads)}
                sparkColor="#10B981"
                onCardClick={() => openDialog('Total Leads', getFilteredLeads(), 'lead', `${totalLeads} leads total`)}
                breakdown={[
                  {
                    label: 'New this month',
                    color: '#10B981',
                    value: leadsThisMonth,
                    onClick: () => {
                      const now = new Date();
                      const items = getFilteredLeads().filter((l) => {
                        const d = new Date(l.createdAt);
                        return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
                      });
                      openDialog('New Leads This Month', items, 'lead', `${items.length} leads`);
                    },
                  },
                  {
                    label: 'Critical',
                    color: '#EF4444',
                    value: criticalLeads,
                    onClick: () => {
                      const items = getFilteredLeads().filter((l) => l.trackingStatus === 'critical');
                      openDialog('Critical Leads', items, 'lead', `${items.length} leads need urgent attention`);
                    },
                  },
                  {
                    label: 'Waiting',
                    color: '#F59E0B',
                    value: waitingLeads,
                    onClick: () => {
                      const items = getFilteredLeads().filter((l) => l.trackingStatus === 'waiting');
                      openDialog('Waiting Leads', items, 'lead', `${items.length} leads waiting for proposal`);
                    },
                  },
                  {
                    label: 'High Priority',
                    color: '#8B5CF6',
                    value: highPriorityLeads,
                    onClick: () => {
                      const items = getFilteredLeads().filter((l) => l.priority === 'high' || l.priority === 'very high');
                      openDialog('High Priority Leads', items, 'lead', `${items.length} leads`);
                    },
                  },
                ]}
              />

              <StatCard
                icon={<svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6M8 13h8M8 17h6"/></svg>}
                iconBg="#F5F3FF"
                iconColor="#8B5CF6"
                label="Total Proposals"
                value={totalProposals}
                change={`${proposalApproval}%`}
                changePositive={proposalApproval >= 50}
                changeLabel="approval rate"
                sparkData={sparkFromCreatedAt(proposals)}
                sparkColor="#8B5CF6"
                onCardClick={() => openDialog('Total Proposals', getFilteredProposals(), 'proposal', `${totalProposals} proposals total`)}
                breakdown={[
                  {
                    label: 'Sent this month',
                    color: '#8B5CF6',
                    value: proposalsSent,
                    onClick: () => {
                      const now = new Date();
                      const items = getFilteredProposals().filter((p) => {
                        if (!['released', 'customer_approved', 'customer_rejected'].includes(p.status)) return false;
                        const d = new Date(p.updatedAt);
                        return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
                      });
                      openDialog('Proposals Sent This Month', items, 'proposal', `${items.length} proposals`);
                    },
                  },
                  {
                    label: 'Draft',
                    color: '#60A5FA',
                    value: draftProposals,
                    onClick: () => {
                      const items = getFilteredProposals().filter((p) => p.status === 'draft');
                      openDialog('Draft Proposals', items, 'proposal', `${items.length} drafts`);
                    },
                  },
                  {
                    label: 'Approved',
                    color: '#10B981',
                    value: approvedProposals,
                    onClick: () => {
                      const items = getFilteredProposals().filter((p) => p.status === 'customer_approved' || p.status === 'approved_proposal_owner');
                      openDialog('Approved Proposals', items, 'proposal', `${items.length} approved`);
                    },
                  },
                ]}
              />

              <StatCard
                icon={<svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="7" width="18" height="13" rx="2"/><path d="M8 7V4h8v3"/></svg>}
                iconBg="#EFF6FF"
                iconColor="#3B82F6"
                label="Active Projects"
                value={totalProjects}
                change={`${projectsThisMonth}`}
                changePositive={projectsThisMonth >= 0}
                changeLabel="started this month"
                sparkData={sparkFromCreatedAt(projects)}
                sparkColor="#3B82F6"
                onCardClick={() => openDialog('All Projects', getFilteredProjects(), 'project', `${totalProjects} projects total`)}
                breakdown={[
                  {
                    label: 'Started this month',
                    color: '#3B82F6',
                    value: projectsThisMonth,
                    onClick: () => {
                      const now = new Date();
                      const items = getFilteredProjects().filter((p) => {
                        const d = new Date(p.createdAt);
                        return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
                      });
                      openDialog('Projects Started This Month', items, 'project', `${items.length} projects`);
                    },
                  },
                  {
                    label: 'On Hold',
                    color: '#F59E0B',
                    value: onHoldProjects,
                    onClick: () => {
                      const items = getFilteredProjects().filter((p) => p.projectStatus === 'on_hold');
                      openDialog('On Hold Projects', items, 'project', `${items.length} projects on hold`);
                    },
                  },
                  {
                    label: 'Completed',
                    color: '#10B981',
                    value: completedProjects,
                    onClick: () => {
                      const items = getFilteredProjects().filter((p) => p.projectStatus === 'completed');
                      openDialog('Completed Projects', items, 'project', `${items.length} projects`);
                    },
                  },
                  {
                    label: 'High Priority',
                    color: '#8B5CF6',
                    value: highPriorityProjects,
                    onClick: () => {
                      const items = getFilteredProjects().filter((p) => p.priority === 'high' || p.priority === 'very high');
                      openDialog('High Priority Projects', items, 'project', `${items.length} projects`);
                    },
                  },
                ]}
              />

              <StatCard
                icon={<svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><path d="M8 12l3 3 5-6"/></svg>}
                iconBg={hexToRgba(accentColor, 0.12)}
                iconColor={accentColor}
                label="Conversion Rate"
                value={`${conversionRate}%`}
                change={`${approvedProposals}`}
                changePositive={true}
                changeLabel="approvals"
                sparkData={sparkFromCreatedAt(leads, (l) => l?.trackingStatus === 'converted')}
                sparkColor={accentColor}
                onCardClick={() => {
                  const items = getFilteredLeads().filter((l) => l.trackingStatus === 'converted');
                  openDialog('Converted Leads', items, 'lead', `${items.length} leads converted to projects`);
                }}
                breakdown={[
                  {
                    label: 'Converted',
                    color: accentColor,
                    value: leadStatusData._raw.converted,
                    onClick: () => {
                      const items = getFilteredLeads().filter((l) => l.trackingStatus === 'converted');
                      openDialog('Converted Leads', items, 'lead', `${items.length} leads`);
                    },
                  },
                  {
                    label: 'Lost',
                    color: '#6B7280',
                    value: leadStatusData._raw.lost,
                    onClick: () => {
                      const items = getFilteredLeads().filter((l) => l.trackingStatus === 'lost');
                      openDialog('Lost Leads', items, 'lead', `${items.length} leads`);
                    },
                  },
                  {
                    label: 'Recently Sent',
                    color: '#60A5FA',
                    value: leadStatusData._raw.recently_sent,
                    onClick: () => {
                      const items = getFilteredLeads().filter((l) => l.trackingStatus === 'recently_sent');
                      openDialog('Recently Sent', items, 'lead', `${items.length} leads`);
                    },
                  },
                ]}
              />
            </div>

            {/* Monthly Lead Creation chart (full width) */}
            <div className="bg-white rounded-2xl border border-gray-200/80 shadow-[0_1px_2px_rgba(15,23,42,0.04)] p-5">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: hexToRgba(accentColor, 0.12), color: accentColor }}>
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 3v18h18" />
                      <path d="M7 14l4-4 4 4 5-7" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900 leading-tight">Monthly Lead Creation</p>
                    <p className="text-[11.5px] text-gray-500 mt-0.5">Click any point to explore that month's leads</p>
                  </div>
                </div>
                <span className="px-2.5 py-1 rounded-md bg-gray-50 text-[11px] font-medium text-gray-600 border border-gray-200">
                  Last 6 Months
                </span>
              </div>
              <div className="h-[300px]">
                <Line data={monthlyChartData} options={monthlyChartOptions} />
              </div>
            </div>

            {/* Status donut grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <DonutCard
                icon={<svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><path d="M9 12l2 2 4-4"/></svg>}
                iconBg="#ECFDF5"
                iconColor="#10B981"
                title="Lead Status"
                subtitle="Click chart slice or row to explore"
                chartData={leadStatusData}
                sliceActions={[
                  () => { const items = getFilteredLeads().filter((l) => l.trackingStatus === 'new'); openDialog('New Leads', items, 'lead', `${items.length} leads`); },
                  () => { const items = getFilteredLeads().filter((l) => l.trackingStatus === 'waiting'); openDialog('Waiting Leads', items, 'lead', `${items.length} leads`); },
                  () => { const items = getFilteredLeads().filter((l) => l.trackingStatus === 'recently_sent'); openDialog('Recently Sent Leads', items, 'lead', `${items.length} leads`); },
                  () => { const items = getFilteredLeads().filter((l) => l.trackingStatus === 'critical'); openDialog('Critical Leads', items, 'lead', `${items.length} leads`); },
                  () => { const items = getFilteredLeads().filter((l) => l.trackingStatus === 'follow_up'); openDialog('Follow Up Leads', items, 'lead', `${items.length} leads`); },
                  () => { const items = getFilteredLeads().filter((l) => l.trackingStatus === 'converted'); openDialog('Converted Leads', items, 'lead', `${items.length} leads`); },
                  () => { const items = getFilteredLeads().filter((l) => l.trackingStatus === 'lost'); openDialog('Lost Leads', items, 'lead', `${items.length} leads`); },
                ]}
                breakdown={[
                  {
                    label: 'New',
                    color: '#4ADE80',
                    value: pct(leadStatusData._raw.new, totalLeads),
                    onClick: () => {
                      const items = getFilteredLeads().filter((l) => l.trackingStatus === 'new');
                      openDialog('New Leads', items, 'lead', `${items.length} new leads`);
                    },
                  },
                  {
                    label: 'Critical',
                    color: '#EF4444',
                    value: pct(leadStatusData._raw.critical, totalLeads),
                    onClick: () => {
                      const items = getFilteredLeads().filter((l) => l.trackingStatus === 'critical');
                      openDialog('Critical Leads', items, 'lead', `${items.length} leads`);
                    },
                  },
                  {
                    label: 'Waiting',
                    color: '#38BDF8',
                    value: pct(leadStatusData._raw.waiting, totalLeads),
                    onClick: () => {
                      const items = getFilteredLeads().filter((l) => l.trackingStatus === 'waiting');
                      openDialog('Waiting Leads', items, 'lead', `${items.length} leads`);
                    },
                  },
                  {
                    label: 'High Priority',
                    color: '#8B5CF6',
                    value: pct(highPriorityLeads, totalLeads),
                    onClick: () => {
                      const items = getFilteredLeads().filter((l) => l.priority === 'high' || l.priority === 'very high');
                      openDialog('High Priority Leads', items, 'lead', `${items.length} leads`);
                    },
                  },
                ]}
              />

              <DonutCard
                icon={<svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6"/></svg>}
                iconBg="#F5F3FF"
                iconColor="#8B5CF6"
                title="Proposal Status"
                subtitle="Click chart slice or row to explore"
                chartData={proposalStatusData}
                sliceActions={[
                  () => { const items = getFilteredProposals().filter((p) => p.status === 'draft'); openDialog('Draft Proposals', items, 'proposal', `${items.length} drafts`); },
                  () => { const items = getFilteredProposals().filter((p) => p.status === 'released'); openDialog('Released Proposals', items, 'proposal', `${items.length} proposals`); },
                  () => { const items = getFilteredProposals().filter((p) => p.status === 'customer_approved'); openDialog('Client-Approved Proposals', items, 'proposal', `${items.length} proposals`); },
                  () => { const items = getFilteredProposals().filter((p) => p.status === 'customer_rejected'); openDialog('Customer-Rejected Proposals', items, 'proposal', `${items.length} proposals`); },
                  () => { const items = getFilteredProposals().filter((p) => p.status === 'not_released'); openDialog('Not Released Proposals', items, 'proposal', `${items.length} proposals`); },
                  () => { const items = getFilteredProposals().filter((p) => p.status === 'approved_proposal_owner'); openDialog('Owner-Approved Proposals', items, 'proposal', `${items.length} proposals`); },
                  () => { const items = getFilteredProposals().filter((p) => p.status === 'rejected_proposal_owner'); openDialog('Owner-Rejected Proposals', items, 'proposal', `${items.length} proposals`); },
                ]}
                breakdown={[
                  {
                    label: 'Sent',
                    color: '#4ADE80',
                    value: pct(proposalStatusData._raw.released, totalProposals),
                    onClick: () => {
                      const items = getFilteredProposals().filter((p) => p.status === 'released');
                      openDialog('Sent Proposals', items, 'proposal', `${items.length} proposals`);
                    },
                  },
                  {
                    label: 'Draft',
                    color: '#60A5FA',
                    value: pct(proposalStatusData._raw.draft, totalProposals),
                    onClick: () => {
                      const items = getFilteredProposals().filter((p) => p.status === 'draft');
                      openDialog('Draft Proposals', items, 'proposal', `${items.length} drafts`);
                    },
                  },
                  {
                    label: 'Approved',
                    color: '#8B5CF6',
                    value: pct(proposalStatusData._raw.customer_approved + proposalStatusData._raw.approved_proposal_owner, totalProposals),
                    onClick: () => {
                      const items = getFilteredProposals().filter((p) => p.status === 'customer_approved' || p.status === 'approved_proposal_owner');
                      openDialog('Approved Proposals', items, 'proposal', `${items.length} approvals`);
                    },
                  },
                  {
                    label: 'Rejected',
                    color: '#EF4444',
                    value: pct(proposalStatusData._raw.customer_rejected + proposalStatusData._raw.rejected_proposal_owner, totalProposals),
                    onClick: () => {
                      const items = getFilteredProposals().filter((p) => p.status === 'customer_rejected' || p.status === 'rejected_proposal_owner');
                      openDialog('Rejected Proposals', items, 'proposal', `${items.length} rejections`);
                    },
                  },
                ]}
              />

              <DonutCard
                icon={<svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="7" width="18" height="13" rx="2"/><path d="M8 7V4h8v3"/></svg>}
                iconBg="#EFF6FF"
                iconColor="#3B82F6"
                title="Project Status"
                subtitle="Click chart slice or row to explore"
                chartData={projectStatusData}
                sliceActions={[
                  () => { const items = getFilteredProjects().filter((p) => p.projectStatus === 'active'); openDialog('Active Projects', items, 'project', `${items.length} projects`); },
                  () => { const items = getFilteredProjects().filter((p) => p.projectStatus === 'ready_to_schedule'); openDialog('Ready to Schedule Projects', items, 'project', `${items.length} projects`); },
                  () => { const items = getFilteredProjects().filter((p) => p.projectStatus === 'on_hold'); openDialog('On Hold Projects', items, 'project', `${items.length} projects`); },
                  () => { const items = getFilteredProjects().filter((p) => p.projectStatus === 'completed'); openDialog('Completed Projects', items, 'project', `${items.length} projects`); },
                  () => { const items = getFilteredProjects().filter((p) => p.projectStatus === 'cancelled'); openDialog('Cancelled Projects', items, 'project', `${items.length} projects`); },
                  () => { const items = getFilteredProjects().filter((p) => p.projectStatus === 'archived'); openDialog('Archived Projects', items, 'project', `${items.length} projects`); },
                ]}
                breakdown={[
                  {
                    label: 'Active',
                    color: '#4ADE80',
                    value: pct(projectStatusData._raw.active, totalProjects),
                    onClick: () => {
                      const items = getFilteredProjects().filter((p) => p.projectStatus === 'active');
                      openDialog('Active Projects', items, 'project', `${items.length} projects`);
                    },
                  },
                  {
                    label: 'On Hold',
                    color: '#F59E0B',
                    value: pct(projectStatusData._raw.on_hold, totalProjects),
                    onClick: () => {
                      const items = getFilteredProjects().filter((p) => p.projectStatus === 'on_hold');
                      openDialog('On Hold Projects', items, 'project', `${items.length} projects`);
                    },
                  },
                  {
                    label: 'Completed',
                    color: '#8B5CF6',
                    value: pct(projectStatusData._raw.completed, totalProjects),
                    onClick: () => {
                      const items = getFilteredProjects().filter((p) => p.projectStatus === 'completed');
                      openDialog('Completed Projects', items, 'project', `${items.length} projects`);
                    },
                  },
                  {
                    label: 'Cancelled',
                    color: '#EF4444',
                    value: pct(projectStatusData._raw.cancelled, totalProjects),
                    onClick: () => {
                      const items = getFilteredProjects().filter((p) => p.projectStatus === 'cancelled');
                      openDialog('Cancelled Projects', items, 'project', `${items.length} projects`);
                    },
                  },
                ]}
              />
            </div>
          </div>
        )}
      </div>

      <DetailDialog
        open={dialog.open}
        onClose={closeDialog}
        title={dialog.title}
        subtitle={dialog.subtitle}
        items={dialog.items}
        type={dialog.type}
        accentColor={accentColor}
      />

      <NotificationsDialog open={showNotifications} onClose={() => setShowNotifications(false)} />

      <EditProfileDialog isOpen={showEditProfile} onClose={() => setShowEditProfile(false)} />
    </DashboardLayout>
  );
};

export default DashboardHome;
